import { completeMagicLink, currentIdToken, signOut, startCodeSignIn } from './auth';
import { config } from './config';
import { bootWithJwt, clearIntercom, goToHelpCenter, mintAndBoot } from './intercom';

enum ViewId {
    Loading = 'view-loading',
    Choice = 'view-choice',
    Email = 'view-email',
    Code = 'view-code',
    SignedIn = 'view-signed-in',
    Error = 'view-error',
}

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

const show = (id: ViewId): void => {
    Object.values(ViewId).forEach((view) => $(view).classList.toggle('hidden', view !== id));
};

const fail = (message: string): void => {
    $('error-message').textContent = message;
    show(ViewId.Error);
};

const handleToken = async (idToken: string): Promise<void> => {
    show(ViewId.Loading);
    try {
        await mintAndBoot(idToken);
        show(ViewId.SignedIn);
    } catch (err) {
        fail((err as Error).message);
    }
};

const wireSignedIn = (): void => {
    $<HTMLButtonElement>('btn-continue').addEventListener('click', () => goToHelpCenter());
    $<HTMLButtonElement>('btn-logout').addEventListener('click', () => {
        clearIntercom();
        signOut();
        show(ViewId.Choice);
    });
};

const wireCustomerFlow = (): void => {
    let submitCode: ((code: string) => Promise<string>) | null = null;

    $<HTMLButtonElement>('btn-customer').addEventListener('click', () => show(ViewId.Email));
    $<HTMLButtonElement>('btn-partner').addEventListener('click', () => {
        // Send partners to the Insights /login route (not root, which the SPA
        // catch-all would rewrite and drop the query). It redirects back to the
        // portal after sign-in when redirect=helpportal is set.
        window.location.href = `${config.insightsUrl.replace(/\/$/, '')}/login?redirect=helpportal`;
    });

    $<HTMLFormElement>('email-form').addEventListener('submit', async (event) => {
        event.preventDefault();
        const email = $<HTMLInputElement>('email-input').value.trim();
        if (!email) return;
        show(ViewId.Loading);
        try {
            submitCode = await startCodeSignIn(email);
            show(ViewId.Code);
        } catch (err) {
            fail((err as Error).message);
        }
    });

    $<HTMLFormElement>('code-form').addEventListener('submit', async (event) => {
        event.preventDefault();
        const code = $<HTMLInputElement>('code-input').value.trim();
        if (!code || !submitCode) return;
        show(ViewId.Loading);
        try {
            await handleToken(await submitCode(code));
        } catch (err) {
            fail((err as Error).message);
        }
    });
};

const init = async (): Promise<void> => {
    wireCustomerFlow();
    wireSignedIn();

    // Partner handoff from Insights: a pre-minted JWT in the URL fragment
    // (fragment keeps it out of server logs / history hops).
    const handoffJwt = new URLSearchParams(window.location.hash.slice(1)).get('jwt');
    if (handoffJwt) {
        window.history.replaceState(null, '', window.location.pathname);
        try {
            bootWithJwt(handoffJwt);
            show(ViewId.SignedIn);
        } catch (err) {
            fail((err as Error).message);
        }
        return;
    }

    const params = new URLSearchParams(window.location.search);
    const email = params.get('email');
    const token = params.get('token');

    // Magic-link arrival.
    if (window.location.pathname.endsWith('/verify') && email && token) {
        show(ViewId.Loading);
        try {
            await handleToken(await completeMagicLink(email, token));
        } catch (err) {
            fail((err as Error).message);
        }
        return;
    }

    // Already signed in → silent re-auth and redirect.
    const existing = await currentIdToken();
    if (existing) {
        await handleToken(existing);
        return;
    }

    show(ViewId.Choice);
};

void init();
